import { useEffect, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Printer, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function StockReport() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/reports/stock")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const exportCSV = () => {
    if (!data?.items?.length) return;
    const rows = [
      ["Item", "Unit", "Quantity", "Min Qty", "Cost", "Value", "Status"],
    ];
    data.items.forEach((i) =>
      rows.push([
        i.name,
        i.unit,
        i.quantity,
        i.min_quantity,
        i.cost,
        i.value,
        i.low_stock ? "LOW" : "OK",
      ]),
    );
    const csv = rows
      .map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `stock-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="p-6 sm:p-8" data-testid="stock-report-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">
            Stock Report
          </h1>
          <p className="text-sm text-[#7A7873] mt-1">
            Closing stock valuation · low-stock alerts.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={exportCSV}
            data-testid="export-stock"
          >
            <Download size={14} className="mr-1.5" />
            CSV
          </Button>
          <Button variant="outline" onClick={() => window.print()}>
            <Printer size={14} className="mr-1.5" />
            Print
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 print-area">
            <Card className="p-4 border-[#EBE8E2] bg-white">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">
                Total Items
              </div>
              <div className="font-heading font-extrabold text-2xl text-[#2D2C2A] mt-1 font-mono-num">
                {data.summary.total_items}
              </div>
            </Card>
            <Card className="p-4 border-[#EBE8E2] bg-white">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">
                Stock Value
              </div>
              <div className="font-heading font-extrabold text-2xl text-[#D95D39] mt-1 font-mono-num">
                {inr(data.summary.total_value)}
              </div>
            </Card>
            <Card className="p-4 border-[#EBE8E2] bg-white">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">
                Low Stock Alerts
              </div>
              <div
                className="font-heading font-extrabold text-2xl mt-1 font-mono-num"
                style={{
                  color:
                    data.summary.low_stock_count > 0 ? "#E76F51" : "#6A7F66",
                }}
                data-testid="low-stock-count"
              >
                {data.summary.low_stock_count}
              </div>
            </Card>
          </div>

          <div className="bg-white border border-[#EBE8E2] rounded-lg overflow-x-auto print-area">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5">Item</th>
                  <th className="text-left px-4 py-2.5">Unit</th>
                  <th className="text-right px-4 py-2.5">Quantity</th>
                  <th className="text-right px-4 py-2.5">Min Qty</th>
                  <th className="text-right px-4 py-2.5">Cost/Unit</th>
                  <th className="text-right px-4 py-2.5">Value</th>
                  <th className="text-right px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((i) => (
                  <tr
                    key={i.id}
                    className="border-t border-[#EBE8E2]"
                    data-testid={`stock-row-${i.id}`}
                  >
                    <td className="px-4 py-2.5 font-medium">{i.name}</td>
                    <td className="px-4 py-2.5 text-[#7A7873]">{i.unit}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num font-semibold">
                      {i.quantity}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-[#7A7873]">
                      {i.min_quantity}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono-num">
                      {inr(i.cost)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono-num font-semibold">
                      {inr(i.value)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {i.low_stock ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#FBE2DE] text-[#E76F51]">
                          <AlertTriangle size={11} /> Low
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#E5EBE3] text-[#6A7F66]">
                          OK
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {data.items.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-10 text-center text-[#7A7873]"
                    >
                      No inventory items.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-[#F4F2EE]">
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-2.5 font-semibold text-right"
                  >
                    Total Stock Value
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono-num font-bold text-[#D95D39]">
                    {inr(data.summary.total_value)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
